import { useEffect, useState } from "react";
import type { PlatformClient } from "../../platform/supabase";

type BadgeEmailStatus = {
  configured:boolean;
  maskedEmail?:string|null;
  verificationStatus?:string|null;
  pendingJobs:number;
  waitingContactJobs:number;
  lastJobStatus?:string|null;
};

function deliveryLabel(status?:string|null){
  const labels:Record<string,string>={
    WAITING_CONTACT:"이메일 등록 대기",
    PENDING:"발송 대기",
    PREPARED:"발송 준비",
    ACCEPTED:"메일 전달",
    RETRYABLE_FAILURE:"재시도 대기",
    PERMANENT_FAILURE:"발송 실패",
    CLAIMED:"Life Passport 연결"
  };
  return status ? labels[status] || status : "발급 전";
}

export function CenterBadgeEmailPanel({
  client,
  schoolId,
  learnerId,
  canManage
}:{
  client:PlatformClient;
  schoolId:string;
  learnerId:string;
  canManage:boolean;
}){
  const [status,setStatus]=useState<BadgeEmailStatus|null>(null);
  const [email,setEmail]=useState("");
  const [busy,setBusy]=useState(false);
  const [error,setError]=useState("");
  const [refresh,setRefresh]=useState(0);

  useEffect(()=>{
    let active=true;
    setError("");
    client.rpc("center_badge_email_status",{
      p_school_id:schoolId,
      p_learner_id:learnerId
    }).then(({data,error})=>{
      if(!active)return;
      if(error){setError(error.message);return;}
      setStatus(data as BadgeEmailStatus);
    });
    return()=>{active=false;};
  },[client,schoolId,learnerId,refresh]);

  async function save(){
    const value=email.trim().toLowerCase();
    if(!value)return;
    setBusy(true);
    setError("");
    const {error}=await client.rpc("center_set_badge_email",{
      p_school_id:schoolId,
      p_learner_id:learnerId,
      p_email:value
    });
    setBusy(false);
    if(error){setError(error.message);return;}
    setEmail("");
    setRefresh((value)=>value+1);
  }

  async function revoke(){
    setBusy(true);
    setError("");
    const {error}=await client.rpc("center_revoke_badge_email",{
      p_school_id:schoolId,
      p_learner_id:learnerId
    });
    setBusy(false);
    if(error){setError(error.message);return;}
    setEmail("");
    setRefresh((value)=>value+1);
  }

  return (
    <section className="badge-email-panel">
      <div className="badge-email-head">
        <div>
          <p className="eyebrow">Life Stamp Delivery</p>
          <strong>배지 수령 이메일</strong>
          <p>배지가 최종 발급되면 등록된 이메일을 첫 접점으로 사용합니다.</p>
        </div>
        <span className={status?.configured ? "status-pill good" : "status-pill"}>
          {status?.configured ? "등록됨" : "미등록"}
        </span>
      </div>

      {error && <p className="error">{error}</p>}

      {status?.configured ? (
        <div className="badge-email-status-row">
          <div>
            <strong>{status.maskedEmail}</strong>
            <small>학교 확인 이메일 · {deliveryLabel(status.lastJobStatus)}</small>
          </div>
          <div className="badge-email-counts">
            {status.pendingJobs>0 && <span>발송 대기 {status.pendingJobs}건</span>}
            {status.waitingContactJobs>0 && <span>연락처 대기 {status.waitingContactJobs}건</span>}
          </div>
        </div>
      ) : (
        <p className="badge-email-empty">
          {status?.waitingContactJobs
            ? `이미 발급된 Life Stamp ${status.waitingContactJobs}건이 이메일 등록을 기다리고 있습니다.`
            : "아직 배지 수령 이메일이 등록되지 않았습니다."}
        </p>
      )}

      {canManage && (
        <div className="badge-email-form">
          <label>
            {status?.configured ? "이메일 변경" : "학생 이메일 등록"}
            <input
              type="email"
              value={email}
              onChange={(event)=>setEmail(event.target.value)}
              placeholder="student@example.com"
              autoComplete="off"
            />
          </label>
          <button className="primary compact-button" disabled={busy || !email.trim()} onClick={save}>
            {busy ? "저장 중…" : status?.configured ? "변경" : "등록"}
          </button>
          {status?.configured && (
            <button className="secondary danger" disabled={busy} onClick={revoke}>
              사용 중지
            </button>
          )}
        </div>
      )}

      <small className="badge-email-note">
        원문 이메일은 교사 화면에 다시 노출하지 않습니다. 학생은 해당 이메일의 Google 계정으로 로그인해야 Life Passport에 연결할 수 있습니다.
      </small>
    </section>
  );
}
